import { useEffect } from 'react'

import logger from '@/client/utils/logger'
import useAiSearchStore from '@/stores/aiSearchStore'
import { isReferenceSearchMode } from '@/stores/searchSelectorStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'

import type {
	UseAudioSearchProps,
	UseAudioSearchReturn,
} from '../SearchBox.types'
import isSupportedStreamingServiceLink from './isSupportedStreamingServiceLink'

export const useAudioSearch = ({
	autocomplete,
	autocompleteState,
	user,
	inputRef,
}: UseAudioSearchProps): UseAudioSearchReturn => {
	const {
		prefetchSearchByAudio,
		prefetchedSearchByAudio,
		removePrefetchedSearchByAudio,
	} = useAiSearchStore()
	const searchType = useSearchSelectorStore((state) => state.searchType)

	// Handle audio URL prefetching
	useEffect(() => {
		if (!autocompleteState?.query) return

		if (!isReferenceSearchMode(searchType)) {
			return
		}

		try {
			if (isSupportedStreamingServiceLink(autocompleteState.query)) {
				prefetchSearchByAudio(autocompleteState.query, !!user)
			}
		} catch (error) {
			logger('Error prefetching audio search', error, {
				query: autocompleteState.query,
			})
		}
	}, [autocompleteState?.query, prefetchSearchByAudio, searchType, user])

	// Handle backspace on audio search
	const handleAudioKeyDown = (event: KeyboardEvent): void => {
		const input = inputRef.current
		if (!input) return

		if (
			event.key === 'Backspace' &&
			input.value === prefetchedSearchByAudio?.title
		) {
			event.preventDefault()
			try {
				removePrefetchedSearchByAudio()
				input.value = ''
				autocomplete.setQuery('')
			} catch (error) {
				logger('Error handling audio search backspace', error, {})
			}
		}
	}

	return {
		handleAudioKeyDown,
	}
}
